/**
 * SVG rendering of the Team Topologies notation (BaseRenderer subclass):
 *  - teams: SOLID resizable boxes (label wrapped inside), distinguished by shape
 *    as well as colour;
 *  - interactions: DASHED glyphs (collaboration = parallelogram, x-as-a-service = triangle,
 *    facilitating = circle) laid over team boundaries, their translucent template fill mixed
 *    over white and drawn opaque;
 *  - flow: a dashed "flow of change" arrow;
 *  - annotations: a BPMN-style open bracket with free text, and the dashed
 *    association connecting an annotation to the element it is attached to.
 *
 * Colours/shapes come from the single notation source (`@miragon/team-topologies-schema-model`).
 */

import BaseRenderer from "diagram-js/lib/draw/BaseRenderer";
import {
  append as svgAppend,
  attr as svgAttr,
  classes as svgClasses,
  create as svgCreate,
} from "tiny-svg";
import type EventBus from "diagram-js/lib/core/EventBus";
import type { ConnectionLike, ElementLike, ShapeLike } from "diagram-js/lib/model/Types";
import {
  FLOW_SPEC,
  INTERACTION_MODE_SPECS,
  TEAM_TYPE_SPECS,
  dashArray,
} from "@miragon/team-topologies-schema-model";
import { MIRAGON } from "../theme/index.js";
import { interactionPaint } from "./colour.js";
import { FONT, STROKE_MUTED, TT_RENDER_PRIORITY } from "./styles.js";
import {
  HIGH_CONTRAST_UNDERLAY_CLASS,
  UNDERLAY_EXTRA_WIDTH,
  contourOnUnderlay,
} from "./underlay.js";
import {
  LINE_HEIGHT,
  flowHeadWidth,
  labelLayout,
  wrapLabel,
  type LabelLayout,
} from "./label-layout.js";
import {
  isTtAnnotation,
  isTtAssociation,
  isTtElement,
  isTtFlow,
  isTtInteraction,
  isTtTeam,
  type TtAnnotation,
  type TtFlow,
  type TtInteraction,
  type TtTeam,
} from "../model/di-types.js";

type Attrs = Record<string, string | number>;

export default class TeamTopologiesRenderer extends BaseRenderer {
  static $inject = ["eventBus"];

  constructor(eventBus: EventBus) {
    super(eventBus, TT_RENDER_PRIORITY);
  }

  override canRender(element: ElementLike): boolean {
    return isTtElement(element) || isTtAssociation(element);
  }

  override drawShape(visuals: SVGElement, element: ShapeLike): SVGElement {
    if (isTtTeam(element)) return this.drawTeam(visuals, element);
    if (isTtInteraction(element)) return this.drawInteraction(visuals, element);
    if (isTtFlow(element)) return this.drawFlow(visuals, element);
    if (isTtAnnotation(element)) return this.drawAnnotation(visuals, element);
    const rect = svgAttr(svgCreate("rect"), {
      width: element.width,
      height: element.height,
      fill: MIRAGON.linie,
    });
    svgAppend(visuals, rect);
    return rect;
  }

  override drawConnection(visuals: SVGElement, connection: ConnectionLike): SVGElement {
    const line = svgAttr(svgCreate("path"), {
      d: waypointsPath(connection.waypoints),
      fill: "none",
      stroke: STROKE_MUTED,
      "stroke-width": 1.5,
      "stroke-dasharray": "4 4",
      "stroke-linecap": "round",
    });
    svgAppend(visuals, line);
    return line;
  }

  override getShapePath(shape: ShapeLike): string {
    const { x, y, width, height } = shape;
    return `M${x},${y}l${width},0l0,${height}l${-width},0z`;
  }

  override getConnectionPath(connection: ConnectionLike): string {
    return waypointsPath(connection.waypoints);
  }

  // --- teams ---------------------------------------------------------------

  private drawTeam(visuals: SVGElement, team: TtTeam): SVGElement {
    const spec = TEAM_TYPE_SPECS[team.teamType] ?? TEAM_TYPE_SPECS["stream-aligned"];
    const w = Math.max(team.width, 1);
    const h = Math.max(team.height, 1);
    const sw = spec.strokeWidth;
    const dash = dashArray(spec.strokeStyle, sw);
    const outline = this.appendOutline(
      visuals,
      (attrs) => this.teamOutline(spec.shape, w, h, sw, attrs),
      { fill: team.fill ?? spec.fill },
      {
        stroke: team.stroke ?? spec.stroke,
        "stroke-width": sw,
        ...(dash ? { "stroke-dasharray": dash } : {}),
        "stroke-linejoin": "round",
      },
      contourOnUnderlay(spec.stroke, team.stroke),
    );
    this.appendLabel(visuals, team.ttLabel ?? "", labelLayout(team));
    return outline;
  }

  private teamOutline(kind: string, w: number, h: number, sw: number, attrs: Attrs): SVGElement {
    const i = sw / 2;
    const iw = w - sw;
    const ih = h - sw;
    if (kind === "octagon") {
      const c = Math.min(iw, ih) * 0.29;
      return svgAttr(svgCreate("polygon"), { points: octagon(i, iw, ih, c), ...attrs });
    }
    const rx = kind === "rect" ? 4 : Math.min(20, ih / 2, iw / 2);
    return svgAttr(svgCreate("rect"), { x: i, y: i, width: iw, height: ih, rx, ry: rx, ...attrs });
  }

  // --- interactions --------------------------------------------------------

  private drawInteraction(visuals: SVGElement, el: TtInteraction): SVGElement {
    const spec = INTERACTION_MODE_SPECS[el.mode] ?? INTERACTION_MODE_SPECS.collaboration;
    const w = Math.max(el.width, 1);
    const h = Math.max(el.height, 1);
    const sw = 2;
    const paint = interactionPaint(el);
    const glyph = this.appendOutline(
      visuals,
      (attrs) => interactionGlyph(spec.shape, w, h, sw, attrs),
      {
        fill: paint.fill,
        ...(paint.fillOpacity < 1 ? { "fill-opacity": paint.fillOpacity } : {}),
      },
      {
        stroke: el.stroke ?? spec.stroke,
        "stroke-width": sw,
        "stroke-dasharray": dashArray(spec.strokeStyle, sw) || "6 4",
        "stroke-linejoin": "round",
      },
      contourOnUnderlay(spec.stroke, el.stroke),
    );
    this.appendLabel(visuals, el.ttLabel ?? "", labelLayout(el));
    return glyph;
  }

  /**
   * The shape's fill and contour as one element, as the templates draw it. A contour on the
   * high-contrast underlay takes two: the fill, whose stroke is the underlay, and the contour above
   * it, so the underlay shows on both sides of the contour. The fill stays the visual's first
   * element, which diagram-js's connect and drop markers tint.
   */
  private appendOutline(
    visuals: SVGElement,
    shape: (attrs: Attrs) => SVGElement,
    area: Attrs,
    contour: Attrs,
    onUnderlay: boolean,
  ): SVGElement {
    if (!onUnderlay) {
      const outline = shape({ ...area, ...contour });
      svgAppend(visuals, outline);
      return outline;
    }
    const underlay = shape({
      ...area,
      ...contour,
      stroke: "none",
      "stroke-width": Number(contour["stroke-width"]) + UNDERLAY_EXTRA_WIDTH,
    });
    svgClasses(underlay).add(HIGH_CONTRAST_UNDERLAY_CLASS);
    svgAppend(visuals, underlay);
    svgAppend(visuals, shape({ fill: "none", ...contour }));
    return underlay;
  }

  // --- flow of change ------------------------------------------------------

  private drawFlow(visuals: SVGElement, el: TtFlow): SVGElement {
    const w = Math.max(el.width, 1);
    const h = Math.max(el.height, 1);
    const sw = FLOW_SPEC.strokeWidth;
    const i = sw / 2;
    const head = flowHeadWidth(w, h);
    const shaft = h * 0.5;
    const top = (h - shaft) / 2;
    const bot = top + shaft;
    const pts = [
      [i, top],
      [w - head, top],
      [w - head, i],
      [w - i, h / 2],
      [w - head, h - i],
      [w - head, bot],
      [i, bot],
    ]
      .map((p) => p.join(","))
      .join(" ");
    const arrow = svgAttr(svgCreate("polygon"), {
      points: pts,
      fill: "none",
      stroke: FLOW_SPEC.stroke,
      "stroke-width": sw,
      "stroke-dasharray": dashArray(FLOW_SPEC.strokeStyle, sw) || "6 4",
      "stroke-linejoin": "round",
    });
    svgAppend(visuals, arrow);
    this.appendLabel(visuals, el.ttLabel ?? "", labelLayout(el));
    return arrow;
  }

  // --- annotations ---------------------------------------------------------

  private drawAnnotation(visuals: SVGElement, el: TtAnnotation): SVGElement {
    const w = Math.max(el.width, 1);
    const h = Math.max(el.height, 1);
    const sw = 1.5;
    const i = sw / 2;
    const arm = Math.min(14, w / 2);
    const bracket = svgAttr(svgCreate("path"), {
      d: `M${arm},${i} L${i},${i} L${i},${h - i} L${arm},${h - i}`,
      fill: "none",
      stroke: STROKE_MUTED,
      "stroke-width": sw,
      "stroke-linejoin": "round",
    });
    svgAppend(visuals, bracket);
    this.appendLabel(visuals, el.ttLabel ?? "", labelLayout(el));
    return bracket;
  }

  // --- shared label rendering (wrapped, no halo) ---------------------------

  private appendLabel(visuals: SVGElement, text: string, layout: LabelLayout): void {
    const { box, fontSize } = layout;
    const lines = wrapLabel(text, box.width, fontSize);
    const lineHeight = fontSize * LINE_HEIGHT;
    const centred = layout.align === "center";
    const x = centred ? box.x + box.width / 2 : box.x;
    const startY = centred
      ? box.y + box.height / 2 - ((lines.length - 1) * lineHeight) / 2
      : box.y + lineHeight / 2;
    lines.forEach((ln, idx) => {
      if (!ln) return;
      const t = svgAttr(svgCreate("text"), {
        x,
        y: startY + idx * lineHeight,
        "font-family": FONT.family,
        "font-size": fontSize,
        fill: layout.fill,
        "text-anchor": centred ? "middle" : "start",
        "dominant-baseline": "central",
        "font-weight": layout.fontWeight,
        ...(layout.letterSpacing ? { "letter-spacing": layout.letterSpacing } : {}),
      });
      t.textContent = ln;
      svgAppend(visuals, t);
    });
  }
}

// --- helpers ---------------------------------------------------------------

function interactionGlyph(
  shape: string,
  w: number,
  h: number,
  sw: number,
  attrs: Attrs,
): SVGElement {
  const i = sw / 2;
  if (shape === "circle") {
    return svgAttr(svgCreate("ellipse"), {
      cx: w / 2,
      cy: h / 2,
      rx: (w - sw) / 2,
      ry: (h - sw) / 2,
      ...attrs,
    });
  }
  if (shape === "triangle") {
    // Point up — the tip indicates the direction the service is provided.
    const pts = `${w / 2},${i} ${w - i},${h - i} ${i},${h - i}`;
    return svgAttr(svgCreate("polygon"), { points: pts, ...attrs });
  }
  const skew = Math.min(w * 0.26, h * 0.85);
  const pts = `${i + skew},${i} ${w - i},${i} ${w - i - skew},${h - i} ${i},${h - i}`;
  return svgAttr(svgCreate("polygon"), { points: pts, ...attrs });
}

function octagon(i: number, w: number, h: number, c: number): string {
  return [
    [i + c, i],
    [i + w - c, i],
    [i + w, i + c],
    [i + w, i + h - c],
    [i + w - c, i + h],
    [i + c, i + h],
    [i, i + h - c],
    [i, i + c],
  ]
    .map((p) => p.join(","))
    .join(" ");
}

function waypointsPath(waypoints: ReadonlyArray<{ x: number; y: number }>): string {
  return waypoints.map((point, idx) => `${idx === 0 ? "M" : "L"}${point.x},${point.y}`).join(" ");
}
